import type { ResourceGuide } from "./resourceGuides";

export const LAUNCH_ARTICLES: readonly ResourceGuide[] = [
  {
    "path": "/resources/business-email-migration-checklist",
    "eyebrow": "Business email migration",
    "publishedAt": "2026-09-09",
    "updatedAt": "2026-09-09",
    "title": "Moving business email: what to check before changing providers.",
    "description": "A practical checklist for protecting your domain, mailbox history and daily work when moving business email to a new provider.",
    "introduction": [
      "A business email move affects more than the inbox. An address might receive customer orders, reset an account password, send website enquiries or appear on invoices. The move is ready when those jobs work at the destination—not simply when an administrator has changed the mail records.",
      "Before choosing a cutover date, write down what exists, what will move and who will check the result. The checklist below is designed for a business owner and the person who manages the company’s email to complete together."
    ],
    "sections": [
      {
        "id": "ownership",
        "title": "1. Confirm who controls the domain and DNS",
        "paragraphs": [
          "Domain registration, DNS hosting, website hosting and email can be supplied by different companies. Record which account controls each service and confirm that an authorised person can sign in. Keep an alternative recovery address that will remain accessible during the move.",
          "Changing email providers does not normally require transferring the domain or moving the website. Save a copy of the DNS zone before making changes, and identify the specific mail records the new provider needs. Do not replace an entire DNS zone with an email setup template."
        ],
        "bullets": [
          "Name the person authorised to approve mail-routing changes.",
          "Record the existing MX records and their time-to-live values.",
          "Identify website, payment, verification and other DNS records that must remain in place."
        ]
      },
      {
        "id": "inventory",
        "title": "2. Inventory addresses, data and hidden senders",
        "paragraphs": [
          "List active mailboxes and their data volumes, then account for shared addresses, aliases, groups, forwarders and automatic replies. An address such as accounts@example.com may be an alias today but need a separate mailbox tomorrow. Decide that mapping before anyone starts copying data.",
          "Ask each department which systems send email. Website forms, invoicing software, booking systems, scanners and newsletters may still rely on the old provider after employee mailboxes have moved."
        ],
        "bullets": [
          "Check whether any messages are stored only on a computer or phone.",
          "Identify delegated access, shared calendars, contacts and tasks separately.",
          "Record retention, export and archive requirements with the responsible person."
        ]
      },
      {
        "id": "scope",
        "title": "3. Agree exactly what the migration includes",
        "paragraphs": [
          "Ask for a written migration scope that names the source, destination, supported data types, exclusions and validation process. Do not assume that copying an inbox also transfers signatures, rules, contacts, calendars or files.",
          "Microsoft’s IMAP migration documentation, linked below, gives a useful example of this boundary: its IMAP method copies email folders, but not contacts, calendar items or tasks. Confirm the capabilities of the actual migration method proposed for your business.",
          "Discuss authentication requirements, unusually large messages, folder mapping, duplicate handling and any user work needed after the move. Agree how source data will be backed up or exported, and confirm that the export can be read before relying on it."
        ]
      },
      {
        "id": "pilot",
        "title": "4. Test a representative mailbox first",
        "paragraphs": [
          "Choose a pilot mailbox that reflects real work: recent and older messages, nested folders, sent mail, attachments and the devices the team uses. Compare representative messages and folders at the destination. Counts are useful, but differences in folder and label handling can make a simple total misleading.",
          "Test sign-in, replies, search, attachments, inbound delivery and outbound authentication. Include a shared address or application sender if it is important to the business. Record unresolved problems and assign an owner before expanding the move."
        ]
      },
      {
        "id": "cutover",
        "title": "5. Plan cutover, overlap and recovery",
        "paragraphs": [
          "Agree a change window, the person publishing DNS, the person checking delivery and the support contact for users. Prepare destination mailboxes and authentication records first. Review caching and TTLs in advance; reducing a TTL at the moment of cutover does not immediately expire records already cached elsewhere.",
          "Keep the old service available for an agreed overlap period and arrange a final synchronisation where the migration method supports it. Some messages can still arrive at the old system while senders use cached DNS.",
          "Write down the conditions that would trigger a pause or rollback. Restoring old MX records changes routing; it does not automatically move messages that have already reached the new provider. A recovery plan must account for mail received by both systems."
        ]
      },
      {
        "id": "signoff",
        "title": "6. Sign off the result before cancelling anything",
        "paragraphs": [
          "Ask named users to confirm their everyday workflows. Check an external incoming message, an external outgoing message and a reply. Verify that application senders and shared addresses work, and inspect the receiving system’s authentication results for controlled test messages.",
          "Close the migration only after the agreed data checks, final synchronisation and access checks are complete. Keep a record of remaining exceptions and when source accounts, credentials and obsolete DNS records will be retired.",
          "If you are considering Envelaro, request a migration assessment with your current provider, mailbox count, approximate data volume and dependencies. Assessment and evaluation come before a production cutover; migration execution requires an active plan and an agreed paid migration package."
        ]
      }
    ],
    "sources": [
      {
        "label": "Microsoft Learn — What an IMAP mailbox migration includes and excludes",
        "url": "https://learn.microsoft.com/en-us/exchange/mailbox-migration/migrating-imap-mailboxes/migrating-imap-mailboxes"
      },
      {
        "label": "Envelaro — Migration scope and process",
        "url": "https://envelaro.com/migration"
      },
      {
        "label": "Envelaro — Billing and trial policy",
        "url": "https://envelaro.com/billing-policy"
      }
    ],
    "related": [
      [
        "Discuss your migration",
        "/migration",
        "Review the process and share the details of your current email service."
      ],
      [
        "Understand email authentication",
        "/resources/spf-dkim-dmarc-explained",
        "See what SPF, DKIM and DMARC do before changing sender records."
      ],
      [
        "Evaluate on a secondary domain",
        "/resources/evaluate-envelaro-secondary-domain",
        "Test the service before considering a production move."
      ]
    ]
  },
  {
    "path": "/resources/spf-dkim-dmarc-explained",
    "eyebrow": "Email authentication for business owners",
    "publishedAt": "2026-09-09",
    "updatedAt": "2026-09-09",
    "title": "SPF, DKIM and DMARC explained for business owners.",
    "description": "Understand what the three email authentication controls do, what they cannot prove, and what to ask the person managing your business domain.",
    "introduction": [
      "An email can display your company’s name without being sent by your company. SPF, DKIM and DMARC help receiving systems check the sending identity behind a message. They work together, but they answer different questions.",
      "You do not need to memorise DNS syntax to oversee your business email. You do need a list of legitimate senders, someone responsible for the records, and evidence that the messages your business sends are authenticating correctly."
    ],
    "sections": [
      {
        "id": "spf",
        "title": "SPF: is this server authorised to send?",
        "paragraphs": [
          "Sender Policy Framework lets a domain publish which systems may send using that domain in the SMTP envelope sender—the address involved in handling delivery failures. The receiving system compares the sending server with that policy. This is not necessarily the same domain a person sees in the From line.",
          "Your mailbox provider is only one possible sender. An invoicing service or website may need its own authorised configuration. Ask your administrator to maintain one SPF policy per domain being checked, avoid duplicate SPF records and stay within the protocol’s DNS-lookup limit."
        ],
        "bullets": [
          "List every service that legitimately sends for the business.",
          "Ask which envelope-sender domain each service uses.",
          "Remove obsolete authorisations after confirming that the service is no longer needed."
        ]
      },
      {
        "id": "dkim",
        "title": "DKIM: does the message have a valid signature?",
        "paragraphs": [
          "DomainKeys Identified Mail adds a digital signature to a message. A receiving system uses a public key in DNS to check the signed headers and body. The signing system keeps the corresponding private key.",
          "A selector tells the receiver where to find the public key. It is a provider-supplied identifier, not a password. A business can use different selectors for different sending platforms. Publishing a key is only part of setup: the platform must actually sign outgoing mail with the matching key.",
          "A valid signature is evidence about the signer and signed content. It does not prove that a message is honest, harmless or written by a particular employee."
        ]
      },
      {
        "id": "dmarc",
        "title": "DMARC: does authentication match the visible From domain?",
        "paragraphs": [
          "DMARC connects authentication to the domain recipients see in the From address. A message passes when SPF or DKIM passes and the domain used by that successful check aligns with the visible From domain. It does not require both mechanisms to pass, although configuring both gives legitimate mail more resilience.",
          "For example, an invoicing service might pass SPF using its own domain while displaying yours in the From line. That SPF result alone may not satisfy DMARC for your company. The service may need aligned DKIM signing or an aligned envelope-sender configuration.",
          "A DMARC policy can request monitoring, quarantine or rejection for failing messages and can request aggregate reports. Receiving systems still apply their own handling decisions. Review legitimate traffic before strengthening a policy; an immediate move to rejection can disrupt a forgotten but legitimate sender."
        ]
      },
      {
        "id": "limits",
        "title": "What these controls do not do",
        "paragraphs": [
          "Authentication is one part of email security. It does not encrypt message contents, replace account security or guarantee inbox placement. A compromised authorised account can still send harmful mail, and an attacker can register a similar-looking domain.",
          "Forwarding can affect SPF, and changes to a message after signing can affect DKIM. When a message fails, inspect the actual route and receiving-system results before changing the domain’s policy. Avoid weakening a working policy simply to make a dashboard indicator turn green."
        ]
      },
      {
        "id": "questions",
        "title": "Five questions to ask your administrator",
        "paragraphs": [
          "Ask for a short explanation and a controlled test, rather than a screenshot showing that DNS records exist. For each important sending service, a received test message should help establish whether authentication passes and whether it aligns with your domain."
        ],
        "bullets": [
          "Have we inventoried employee mail, website forms, billing software and marketing senders?",
          "Which domain does each service use for SPF, and who maintains that policy?",
          "Does every important service sign with DKIM using the intended domain and selector?",
          "Who reviews DMARC reports and investigates unexpected sources?",
          "What evidence will we check before changing a provider or strengthening the policy?"
        ]
      },
      {
        "id": "next-step",
        "title": "Start with a domain check, then test real mail",
        "paragraphs": [
          "Envelaro’s Email Health tool can help you review public DNS evidence and identify questions to investigate. If you supply a DKIM selector, use the one provided by your actual sending service rather than guessing.",
          "A public DNS check cannot inspect private keys, read every message or prove that all business systems authenticate correctly. Pair the result with controlled message-header tests and, where configured, DMARC aggregate reports. The detailed DKIM and DMARC guides below explain the next steps."
        ]
      }
    ],
    "sources": [
      {
        "label": "RFC 7208 — Sender Policy Framework",
        "url": "https://www.rfc-editor.org/rfc/rfc7208"
      },
      {
        "label": "RFC 6376 — DomainKeys Identified Mail",
        "url": "https://www.rfc-editor.org/rfc/rfc6376"
      },
      {
        "label": "RFC 7489 — DMARC",
        "url": "https://www.rfc-editor.org/rfc/rfc7489"
      },
      {
        "label": "Google Workspace — Set up DMARC and understand alignment",
        "url": "https://knowledge.workspace.google.com/admin/security/set-up-dmarc"
      }
    ],
    "related": [
      [
        "Check your email domain",
        "/tools/email-health",
        "Review public DNS evidence without moving your email."
      ],
      [
        "Read the DKIM guide",
        "/resources/dkim-guide",
        "Understand selectors, signing and verification."
      ],
      [
        "Read the DMARC guide",
        "/resources/dmarc-guide",
        "Explore alignment, reporting and a controlled policy rollout."
      ]
    ]
  },
  {
    "path": "/resources/evaluate-envelaro-secondary-domain",
    "eyebrow": "A practical Envelaro evaluation",
    "publishedAt": "2026-09-09",
    "updatedAt": "2026-09-09",
    "title": "How to evaluate Envelaro using a secondary domain before moving your main email.",
    "description": "Plan a small Envelaro evaluation, test everyday email and administration, and decide whether to proceed while your primary email stays with its current provider.",
    "introduction": [
      "You can learn how Envelaro works without starting with the domain customers use every day. A separate domain that your business owns can provide a place to test setup, administration and a small set of everyday email workflows.",
      "The purpose is to gather evidence for a decision. An evaluation should end with a list of what worked, what needs clarification and what a production migration would require. There is no requirement to move your primary email or provide a favourable testimonial."
    ],
    "sections": [
      {
        "id": "choose-domain",
        "title": "1. Choose a genuinely non-critical domain",
        "paragraphs": [
          "Use a secondary or test domain that you own and can administer. Check that it is not receiving orders, used for account recovery or connected to an application that depends on its current email service. “Secondary” does not automatically mean unused.",
          "Record its existing DNS and confirm who can approve changes. Keep the primary business domain’s MX and authentication records unchanged throughout this evaluation. Do not forward production mail or import sensitive customer messages just to populate the test inbox.",
          "A separate domain keeps its mail-routing changes distinct from your primary domain. It does not eliminate every risk: access permissions, shared DNS accounts and the people performing the changes still need care."
        ]
      },
      {
        "id": "prepare",
        "title": "2. Prepare the people and the setup",
        "paragraphs": [
          "Include the person responsible for domain administration and someone who can assess daily use and commercial fit. Agree a few tasks in advance, such as creating an alias, finding an older test message or helping another participant sign in.",
          "For the guided evaluation, submit the evaluation request with your current provider, team size and what you want to learn. Coordinate onboarding before starting the timed trial so support and participants are available.",
          "The trial requires a verified tenant-administrator account, a verified business domain and at least one configured mailbox. Envelaro’s trial dialog shows the missing domain and mailbox steps before activation. Domain ownership verification is a preparation step; complete the displayed mail-routing instructions separately when the service records are ready."
        ]
      },
      {
        "id": "allowance",
        "title": "3. Understand the trial allowance",
        "paragraphs": [
          "The standard card-free trial lasts seven business days after successful activation. Weekends remain accessible and do not count toward those seven days. The displayed expiry is the reference for your workspace.",
          "The trial permits up to three mailboxes, with 1 GB per mailbox and 1 GB of shared file storage. External sending is limited to five recipient deliveries per mailbox per day. Plan a small set of consenting test recipients and leave room for replies; this allowance is not intended for outreach campaigns or load testing.",
          "The trial does not automatically become a paid subscription. Review the current billing policy and plan details before choosing paid service. Migration assessment can be discussed during evaluation, but migration execution requires an active plan and an agreed paid migration package."
        ]
      },
      {
        "id": "test",
        "title": "4. Test a small set of real workflows",
        "paragraphs": [
          "Use harmless sample messages and documents. Once the test domain’s mail records are ready and published, send controlled messages to mailboxes you or participating colleagues can inspect. Test receiving, replying and a small attachment as well as simply pressing Send.",
          "Write down the result of each task and the steps required. If an action fails, capture the time, the relevant error and the domain or mailbox involved; do not share passwords or private message contents in a support request."
        ],
        "bullets": [
          "Administration: understand mailbox and alias setup, access controls and the distinction between administrator and mailbox sign-in.",
          "Everyday mail: check folders, search, replies and the devices or email clients your team needs.",
          "Delivery: verify an incoming message, an outgoing message and a reply; inspect authentication results on a controlled received message.",
          "Support: ask one concrete setup question and assess whether the answer explains the next action."
        ]
      },
      {
        "id": "review",
        "title": "5. Review results before considering a migration",
        "paragraphs": [
          "Meet with the participants and compare the results with the tasks you agreed at the start. Separate configuration questions from missing requirements. A feature demonstrated in another product, a public roadmap or a sales conversation is not a substitute for confirming the workflow you need.",
          "A small trial cannot establish long-term availability, large-mailbox migration performance or every aspect of delivery reputation. If you intend to move production email, request a separate scope covering source access, eligible data, backups, downtime expectations, cutover responsibilities and recovery.",
          "Proceed only when the decision-maker understands the remaining work and the applicable charges. A useful evaluation can also end with a decision to keep the current provider."
        ]
      },
      {
        "id": "finish",
        "title": "6. Close the test deliberately",
        "paragraphs": [
          "Before the trial ends, save any test results or data you need and decide whether to continue with a paid plan. If you stop, review the data-lifecycle policy and ask support about closing the workspace and removing test data.",
          "Review the secondary domain’s DNS as part of closeout. If it will receive mail elsewhere, prepare the intended service before changing routing. Remove obsolete sender authorisations only after confirming that no legitimate service still uses them. Your primary business email can remain with its existing provider throughout."
        ]
      }
    ],
    "sources": [
      {
        "label": "Envelaro — Guided evaluation and participation questions",
        "url": "https://envelaro.com/evaluation"
      },
      {
        "label": "Envelaro — Trial, billing and cancellation policy",
        "url": "https://envelaro.com/billing-policy"
      },
      {
        "label": "Envelaro — Acceptable use and trial sending allowance",
        "url": "https://envelaro.com/acceptable-use"
      },
      {
        "label": "Envelaro — Data lifecycle and export policy",
        "url": "https://envelaro.com/data-lifecycle"
      }
    ],
    "related": [
      [
        "Request a guided evaluation",
        "/evaluation#apply",
        "Tell us what your team needs to test."
      ],
      [
        "Prepare a future migration",
        "/resources/business-email-migration-checklist",
        "Identify data, dependencies and sign-off checks before any production move."
      ],
      [
        "Review current plans",
        "/pricing",
        "Compare the service and capacity you would need after evaluation."
      ]
    ]
  }
];
